import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, Users, UserPlus, Crown, Shield, Pencil, Eye, Trash2 } from 'lucide-react'
import { collaborationService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'

const roleIcon = {
  owner: Crown,
  admin: Shield,
  editor: Pencil,
  viewer: Eye,
}

const CollaborationPage = () => {
  const [loading, setLoading] = useState(true)
  const [workspaces, setWorkspaces] = useState([])
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)
  const [workspaceMembers, setWorkspaceMembers] = useState([])
  const [workspaceSummary, setWorkspaceSummary] = useState(null)
  const [memberRole, setMemberRole] = useState('viewer')
  const [memberUserId, setMemberUserId] = useState('')

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [workspaceForm, setWorkspaceForm] = useState({
    name: '',
    description: '',
    is_default: false,
  })

  const loadWorkspaces = async () => {
    try {
      setLoading(true)
      const data = await collaborationService.getWorkspaces()
      const merged = [
        ...(data.owned_workspaces || []),
        ...(data.shared_workspaces || []).filter((ws) => !(data.owned_workspaces || []).find((owned) => owned.id === ws.id)),
      ]

      setWorkspaces(merged)

      if (!selectedWorkspace && merged.length > 0) {
        setSelectedWorkspace(merged[0])
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load workspaces')
    } finally {
      setLoading(false)
    }
  }

  const loadWorkspaceDetails = async (workspaceId) => {
    try {
      const [membersData, summaryData] = await Promise.all([
        collaborationService.getWorkspaceMembers(workspaceId),
        collaborationService.getWorkspaceSummary(workspaceId),
      ])

      setWorkspaceMembers(membersData.members || [])
      setWorkspaceSummary(summaryData || null)
    } catch (error) {
      toast.error(error.message || 'Failed to load workspace details')
    }
  }

  useEffect(() => {
    loadWorkspaces()
  }, [])

  useEffect(() => {
    if (selectedWorkspace?.id) {
      loadWorkspaceDetails(selectedWorkspace.id)
    }
  }, [selectedWorkspace?.id])

  const handleCreateWorkspace = async (e) => {
    e.preventDefault()

    try {
      const created = await collaborationService.createWorkspace(workspaceForm)
      toast.success('Workspace created')
      setShowCreateModal(false)
      setWorkspaceForm({ name: '', description: '', is_default: false })
      await loadWorkspaces()
      setSelectedWorkspace(created)
    } catch (error) {
      toast.error(error.message || 'Failed to create workspace')
    }
  }

  const handleAddMember = async () => {
    if (!selectedWorkspace?.id) return
    if (!memberUserId.trim()) {
      toast.error('Enter a valid member user ID')
      return
    }

    try {
      await collaborationService.addWorkspaceMember(selectedWorkspace.id, {
        user_id: memberUserId.trim(),
        role: memberRole,
      })
      toast.success('Member added')
      setMemberUserId('')
      setMemberRole('viewer')
      await loadWorkspaceDetails(selectedWorkspace.id)
    } catch (error) {
      toast.error(error.message || 'Failed to add member')
    }
  }

  const handleUpdateMemberRole = async (memberUserId, role) => {
    if (!selectedWorkspace?.id) return

    try {
      await collaborationService.updateWorkspaceMember(selectedWorkspace.id, memberUserId, { role })
      toast.success('Member role updated')
      await loadWorkspaceDetails(selectedWorkspace.id)
    } catch (error) {
      toast.error(error.message || 'Failed to update role')
    }
  }

  const handleRemoveMember = async (memberUserId) => {
    if (!selectedWorkspace?.id) return
    if (!window.confirm('Remove this member from the workspace?')) return

    try {
      await collaborationService.removeWorkspaceMember(selectedWorkspace.id, memberUserId)
      toast.success('Member removed')
      await loadWorkspaceDetails(selectedWorkspace.id)
    } catch (error) {
      toast.error(error.message || 'Failed to remove member')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading collaboration spaces..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Collaboration Workspaces</h1>
          <p className="text-gray-600 dark:text-gray-400">Create shared spaces and manage finance access roles.</p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Workspace
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Workspaces</h2>
          {workspaces.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">No workspaces yet.</p>
          ) : (
            <div className="space-y-2">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => setSelectedWorkspace(workspace)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${selectedWorkspace?.id === workspace.id
                    ? 'bg-primary-100 dark:bg-primary-900/40'
                    : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  <p className="font-semibold text-gray-900 dark:text-white">{workspace.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{workspace.description || 'No description'}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="card lg:col-span-2">
          {!selectedWorkspace ? (
            <p className="text-gray-500 dark:text-gray-400">Select a workspace to view members and summary.</p>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedWorkspace.name}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{selectedWorkspace.description || 'No description'}</p>
                </div>

                {workspaceSummary?.your_role && (
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 capitalize">
                    Your role: {workspaceSummary.your_role}
                  </span>
                )}
              </div>

              {workspaceSummary?.stats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Members</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{workspaceSummary.stats.member_count}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Budgets</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{workspaceSummary.stats.budget_count}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Expenses</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{workspaceSummary.stats.expense_count}</p>
                  </div>
                </div>
              )}

              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 space-y-3">
                <h3 className="font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
                  <UserPlus className="h-4 w-4" /> Add member
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    className="input-field md:col-span-2"
                    placeholder="Member user ID"
                    value={memberUserId}
                    onChange={(e) => setMemberUserId(e.target.value)}
                  />
                  <select
                    className="input-field"
                    value={memberRole}
                    onChange={(e) => setMemberRole(e.target.value)}
                  >
                    <option value="viewer">viewer</option>
                    <option value="editor">editor</option>
                    <option value="admin">admin</option>
                  </select>
                </div>
                <button onClick={handleAddMember} className="btn-primary">
                  Invite / Add Member
                </button>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 inline-flex items-center gap-2">
                  <Users className="h-4 w-4" /> Members
                </h3>
                {workspaceMembers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No members found.</p>
                ) : (
                  <div className="space-y-2">
                    {workspaceMembers.map((member) => {
                      const Icon = roleIcon[member.role] || Eye
                      return (
                        <div key={`${member.workspace_id}-${member.user_id}`} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{member.user_id || member.invited_email || 'Unknown user'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Status: {member.status}</p>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 inline-flex items-center gap-1 capitalize">
                              <Icon className="h-3 w-3" /> {member.role}
                            </span>

                            <select
                              className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1 py-1"
                              value={member.role}
                              onChange={(e) => handleUpdateMemberRole(member.user_id, e.target.value)}
                            >
                              <option value="viewer">viewer</option>
                              <option value="editor">editor</option>
                              <option value="admin">admin</option>
                            </select>

                            <button
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="text-red-600 hover:text-red-700"
                              title="Remove member"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Workspace</h3>
            <form onSubmit={handleCreateWorkspace} className="space-y-4">
              <input
                required
                className="input-field"
                placeholder="Workspace name"
                value={workspaceForm.name}
                onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <textarea
                className="input-field"
                rows={3}
                placeholder="Description"
                value={workspaceForm.description}
                onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, description: e.target.value }))}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={workspaceForm.is_default}
                  onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, is_default: e.target.checked }))}
                />
                Mark as default workspace
              </label>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CollaborationPage
